import React, { useEffect, useState } from 'react';
import { ArrowsOutSimple, DownloadSimple } from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Modal } from 'app/components';
import AttendeeView from './attendeeView';
import { exportEventAttendees, getEventAttendees } from '../../api';
import { COLUMNS_CONFIG, SORT_CONFIG } from './constants';
import Button from '../../../../components/button/button';
import * as XLSX from 'xlsx';

const encoded = (input) => input.replace(/[ :]/g, (match) => {
  return {
    ' ': '%20',
    ':': '%3A'
  }[match];
});

const AttendeesTable = ({ stateName, dispatchText }) => {
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [searchText, setSearchText] = useState('');
  const { eventDetails, smpId, attendeesList, attendeesTableData } = useSelector((state) => state[stateName || 'eventDashboard']);
  const { activeRequests } = useSelector((state) => state.app);
  const [id, setId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (activeRequests?.includes('get-event-attendees')) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [activeRequests]);

  useEffect(() => {
    if (smpId) {
      getEventAttendees(smpId, {
        page_no: 0,
        page_size: 10,
        start_date: '2024-12-01 23:00:00',
        end_date: eventDetails?.end_date,
        status: 'BOOKED',
        order_by: 'DESC'
      }, dispatchText);
    }
  }, [smpId,id]);

  const onClickActionItem = (actionItem, row) => {
    if (actionItem.action === 'view') {
      setSelectedAttendee(row);
    }
  };

  const onModalClose = () => {
    setSelectedAttendee(null);
  };

  const onChangeTableData = (data, value) => {
    if (data === 'page_no') {
      getEventAttendees(smpId, { ...attendeesTableData, page_no: value - 1 }, dispatchText);
    } else if (data === 'search') {
      setSearchText(value);
      getEventAttendees(smpId, { ...attendeesTableData, filter: value }, dispatchText);
    } else if (data === 'sort') {
      getEventAttendees(smpId, { ...attendeesTableData, sort: value }, dispatchText);
    }
  };
  
  const shouldShowLoader = loading && !attendeesList?.length;

  if (window.innerWidth <= 768) {
    // Mobile: Show AttendeeView full-screen if selected, otherwise show list
    if (selectedAttendee) {
      return (
        <div className='px-4 py-4 flex-1'>
          <AttendeeView
            id={id}
            setId={setId}
            config={selectedAttendee}
            eventDetails={eventDetails}
            resourceMapping={selectedAttendee?.ticket_details?.[0]?.resource_mapping || []}
            ticketCount={selectedAttendee?.ticket_details?.[0]?.resource_mapping?.filter((item) => item?.is_checked_in)?.length}
            setSelectedAttendee={setSelectedAttendee}
          />
        </div>
      );
    }

    return (
      <div className='px-4 py-4 flex-1'>
        <div className="heading-24 pb-6">Attendee Details</div>
        {attendeesList?.length ? (
          <div className="flex flex-col gap-3">
            {attendeesList.map((item, index) => {
              const ticket = item?.ticket_details?.[0];
              const isChecked = ticket?.resource_mapping?.filter(t => t?.is_checked_in)?.length;
              const total = ticket?.resource_mapping?.length;
              const tier = ticket?.ticket_type;

              return (
                <div
                  key={index}
                  onClick={() => setSelectedAttendee(item)}
                  className="w-full bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm active:scale-[0.98] transition cursor-pointer"
                > 
                  {/* Content */}
                  <div className="flex flex-col flex-1">
                    <div className="font-semibold text-[15px] text-gray-900">
                      {item?.get_to_know_details?.details?.[0]?.full_name || '—'}
                    </div>

                    {/* Badge */}
                    <div className="mt-1">
                      {isChecked === 0 ? (
                        <span className="text-[13px] px-2 py-[2px] rounded-full bg-gray-100 text-gray-500">
                          Yet To Check In
                        </span>
                      ) : isChecked === total ? (
                        <span className="text-[13px] px-2 py-[2px] rounded-full bg-green-100 text-green-700">
                          All Checked In
                        </span>
                      ) : (
                        <span className="text-[13px] px-2 py-[2px] rounded-full bg-pink-100 text-pink-600">
                          {isChecked} • Checked In
                        </span>
                      )}
                    </div>

                    {/* Tier + Count */}
                    <div className="flex items-center gap-2 text-[14px] text-gray-700 mt-1">
                      <span>{tier}</span>
                      <span>•</span>
                      <span>{total}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-16">No tickets sold yet!</div>
        )}
      </div>
    );
  }

  return (
    <div className='px-9 py-9 flex-1'>
      <div className='flex justify-between items-center pb-9'>
        <div className="heading-24">Attendee Details</div>
        {(!shouldShowLoader && attendeesList?.length > 0) ? <Button
          iconLeft={<DownloadSimple DownUp color="#878787" />}
          type="tertiary"
          size="small"
          className="export-button"
          loading={exporting}
          loadingText="Exporting..."
          onClick={async () => {
            setExporting(true);
            const filename = 'attendees_report.xlsx';
            console.log('Preparing to export attendees report as XLSX');
            await exportEventAttendees(smpId, { ...attendeesTableData, page_size: Array.isArray(eventDetails?.smp_detail_entity_set) ? eventDetails?.smp_detail_entity_set.reduce((acc, item) => acc + (item?.quantity || 0), 0) : 10 }, (csvData) => {
              // 1. Prepare Data for XLSX (Array of Arrays format is easiest)
              const { columns, rows } = csvData.data;

              const headers = columns;
              const dataRows = rows.map(rowObject =>
                headers.map(headerKey => {
                  const value = rowObject[headerKey];
                  // Strategy for "all columns as text format": 
                  // Forcing Excel to treat data as text by stringifying it and adding a leading tick mark.
                  // This is especially useful for numbers and IDs (like phone/transaction ID) that should retain leading zeros or full precision.
                  return `${String(value)}`;
                })
              );

              // Combine headers and data rows into a single AoA
              const aoaData = [headers].concat(dataRows);

              // 2. Create Workbook and Worksheet
              const wb = XLSX.utils.book_new();

              // Create the sheet from the Array of Arrays (AoA) data
              const ws = XLSX.utils.aoa_to_sheet(aoaData);

              // Optional: Auto-width columns for better display
              const wscols = headers.map(header => ({ wch: header.length + 5 }));
              ws['!cols'] = wscols;

              XLSX.utils.book_append_sheet(wb, ws, 'Attendees');

              // 3. Generate File Buffer
              // 'array' type output is compatible with the browser Blob constructor
              const wbout = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'array' // Returns ArrayBuffer in browser environment
              });

              // 4. Create a Blob from the ArrayBuffer
              const blob = new Blob([wbout], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              });

              // 5. Create temporary URL and link and trigger download
              const link = document.createElement('a');
              const url = URL.createObjectURL(blob);

              link.href = url;
              link.download = filename;

              document.body.appendChild(link);
              link.click();

              // 6. Clean up
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            });
            setExporting(false);
          }}
        >
          Export report as Sheet
        </Button> : ''}
      </div>
      {attendeesList?.length || searchText ? (
        <Table
          totalPages={attendeesTableData?.total_pages || 1}
          showSearch={true}
          columns={COLUMNS_CONFIG}
          rows={attendeesList}
          actionItems={[{ icon: <ArrowsOutSimple />, action: 'view' }]}
          sorts={SORT_CONFIG}
          selectedPage={(attendeesTableData?.page_no || 0) + 1}
          appliedSorts={attendeesTableData?.sort}
          searchText={searchText}
          onClickActionItem={onClickActionItem}
          onChange={onChangeTableData}
          loading={shouldShowLoader}
        />
      ) : (
        <div className="text-16">No tickets sold yet!</div>
      )}
      <Modal
        hasClose={true}
        onClose={onModalClose}
        isOpen={!!selectedAttendee}
        size="large"
        title={selectedAttendee?.get_to_know_details?.details?.length > 1 ? 'Booking details' : selectedAttendee?.get_to_know_details?.details?.[0]?.full_name}
      >
        <AttendeeView
          id={id}
          setId={setId}
          config={selectedAttendee}
          eventDetails={eventDetails}
          resourceMapping={selectedAttendee?.ticket_details?.[0]?.resource_mapping || []}
          ticketCount={selectedAttendee?.ticket_details?.[0]?.resource_mapping?.filter((item) => item?.is_checked_in)?.length}
          setSelectedAttendee={setSelectedAttendee}
        />
      </Modal>
    </div>
  );
};

export default AttendeesTable;
